import type { HetivoPaginatedResponse } from '../api/pagination.interface';
import type { LocalizedString } from '../localized-string';
import type { GarageKind } from '../supplier/supplier.interface';
import type { TerritoryCurrency } from '../territory/territory.interface';
import type {
  WorkOrder,
  WorkOrderNotScheduledReason,
  WorkOrderStatus,
  WorkOrderTimelineDetails,
  WorkOrderTimelineEvent,
  WorkOrderType,
} from './work-order.interface';

/**
 * The Type filter values on the Work orders list (SWOOP-326, L3). `SCHEDULED` maps to a `SCHEDULED`
 * work order; the other three map to a `NOT_SCHEDULED` work order whose `reason` matches the value.
 * Deliberately NOT the same as {@link WorkOrderType}: this is the four-way UI filter, not the stored
 * two-way type.
 */
export const WORK_ORDER_TYPE_FILTER = {
  CORRECTIVE: 'CORRECTIVE',
  DAMAGE: 'DAMAGE',
  PREVENTIVE: 'PREVENTIVE',
  SCHEDULED: 'SCHEDULED',
} as const;

export type WorkOrderTypeFilter = (typeof WORK_ORDER_TYPE_FILTER)[keyof typeof WORK_ORDER_TYPE_FILTER];

/**
 * Criteria the Work orders list / per-status counts accept (SWOOP-326, L2/L3). All optional.
 *
 * `territoryIds` are resolved to their locations and matched on the work order's `locationId`, exactly
 * as the vehicles list does (a work order has no direct territory — it inherits its location's, D18).
 * `status` is the active tab; the counts endpoint ignores it (it reports every tab). `search` matches
 * the work order number, the vehicle plate, the vehicle's business `vehicleId`, or a make name.
 */
export interface SearchWorkOrderCriteria {
  garageIds?: string[];
  locationIds?: string[];
  search?: string;
  status?: WorkOrderStatus;
  territoryIds?: string[];
  types?: WorkOrderTypeFilter[];
  /** One vehicle's work orders (SWOOP-336, vehicle profile), still within the caller's scope. */
  vehicleId?: string;
  /**
   * Ask for each row's `documentCount` (SWOOP-337, vehicle profile service history only). Off by default:
   * the main Work orders list never shows it, so it costs no count query there. Even when `true`, the
   * server only computes it for a caller holding `documents:list`.
   */
  withDocumentCount?: boolean;
}

/** The vehicle summary shown on a Work orders list row (SWOOP-326, L4). `null` when the vehicle no longer resolves. */
export interface WorkOrderListVehicle {
  id: string;
  /** The make's localized label, or `null` when the make no longer resolves. */
  makeName: LocalizedString | null;
  /** The model name (the vehicle's denormalized `modelCode`). */
  model: string;
  /** The license plate. */
  plate: string;
  /** The business vehicle id (e.g. `FLEET42`), not the Mongo id. */
  vehicleId: string;
  year: number;
}

/** The garage summary shown on a Work orders list row / detail (SWOOP-326, L4). */
export interface WorkOrderListGarage {
  garageKind: GarageKind;
  id: string;
  /** The garage's display name (trade name when set, otherwise legal name). */
  name: string;
}

/**
 * Where a listed work order came from (SWOOP-304), shown under its motive: the issue it was opened from or
 * that was created with it (number + title), or the service reminder it was opened from (interval).
 */
export type WorkOrderListSource =
  | { kind: 'issue'; number: string; title: string | null }
  | { kind: 'reminder'; intervalCode: string; intervalName: string | null };

/** The cost of a closed work order on a list row (SWOOP-336): its invoice incl. VAT in the vehicle's currency. */
export interface WorkOrderListCost {
  amount: number;
  currency: TerritoryCurrency;
}

/** One Work orders list row (SWOOP-326, L4): the display slice of a work order plus its resolved relations. */
export interface WorkOrderListItem {
  /**
   * The cost of a CLOSED work order (SWOOP-336): its invoice incl. VAT (`netAmount + vatAmount`) in the
   * vehicle's currency. `null` while the work order is not closed or has no invoice.
   */
  cost: WorkOrderListCost | null;
  createdAt: Date;
  /**
   * How many live documents the work order has (SWOOP-337, service history Docs column): its own
   * documents plus its Close invoice receipts — the same two sets the work order Documents tab lists.
   * `0` when it has none; `null` when it was not computed: the request did not ask for it
   * (`withDocumentCount`) or the caller cannot list documents (`documents:list`).
   */
  documentCount: number | null;
  expectedCompletionDate: Date | null;
  garage: WorkOrderListGarage | null;
  id: string;
  /** The labels of the items to fix, in order (SWOOP-336, service history "Operations"). */
  itemLabels: string[];
  /** Display number of the bound issue, e.g. `ISS-42` (SWOOP-248 US-8); `null` for a Scheduled work order. */
  issueNumber: string | null;
  notes: string;
  number: string;
  /** Odometer reading when the work order was opened (SWOOP-336); `null` when none was recorded. */
  odometer: number | null;
  /** Odometer reading when the vehicle came back, recorded at close (SWOOP-336); `null` before it is closed. */
  odometerIn: number | null;
  reason: WorkOrderNotScheduledReason | null;
  /** When the vehicle came back, set at close (SWOOP-336, service history date); `null` before it is closed. */
  returnedOn: Date | null;
  /** Where it came from (SWOOP-304); `null` for a work order opened directly with no issue. */
  source: WorkOrderListSource | null;
  status: WorkOrderStatus;
  type: WorkOrderType;
  vehicle: WorkOrderListVehicle | null;
  /** The work order's own date (SWOOP-336, "Opened …" and ordering on the vehicle profile). */
  workOrderDate: Date;
}

/**
 * Per-status tab counts (SWOOP-326, L2). Every tab has its own count, following the active search and
 * filters (and the master selector) but NOT the status filter. `all` is the total across every status.
 */
export interface WorkOrderStatusCounts {
  all: number;
  atGarage: number;
  cancelled: number;
  closed: number;
  done: number;
  open: number;
  received: number;
  /**
   * `true` when a free-text term saturated the bounded vehicle-id lookup (`MAX_RESOLVED_VEHICLE_IDS`),
   * so the counts may undercount vehicle matches beyond the cap. `false` without a term or below the cap.
   */
  searchTruncated: boolean;
}

/**
 * The Work orders list response (SWOOP-326, L4): the standard paginated envelope plus `searchTruncated`,
 * which is `true` when a free-text term saturated the bounded vehicle-id lookup server-side and the page
 * may therefore omit matches beyond the cap. Additive to {@link HetivoPaginatedResponse} — existing
 * clients ignore the extra field.
 */
export interface WorkOrderListResponse extends HetivoPaginatedResponse<WorkOrderListItem> {
  searchTruncated: boolean;
}

/**
 * KPI card counts (SWOOP-326, L1): Open (`OPEN`), At garage (`AT_GARAGE`), Awaiting verification
 * (`DONE`). These follow ONLY the master selector scope — never the search or the filters.
 */
export interface WorkOrderKpis {
  atGarage: number;
  awaitingVerification: number;
  open: number;
}

/** The interval summary shown on a scheduled work order detail (SWOOP-326, L7), read from the plan in force. */
export interface WorkOrderDetailInterval {
  code: string;
  dueDate: Date | null;
  dueOdometer: number | null;
  name: string;
}

/** The vehicle summary on a work order detail (SWOOP-326, L7): the list vehicle plus its location name. */
export interface WorkOrderDetailVehicle extends WorkOrderListVehicle {
  locationName: string;
}

/** The opener summary on a work order detail (SWOOP-326, L7). */
export interface WorkOrderDetailOpenedBy {
  id: string;
  name: string;
}

/**
 * One timeline entry on a work order detail (SWOOP-250, WOW 2): the stored entry with its acting user
 * resolved to `{ id, name }` (or `null` when the user no longer resolves), returned newest-first. `details`
 * is the stored event payload (only send-to-garage carries one so far).
 */
export interface WorkOrderDetailTimelineEntry {
  at: Date;
  by: WorkOrderDetailOpenedBy | null;
  details: WorkOrderTimelineDetails | null;
  type: WorkOrderTimelineEvent;
}

/**
 * The resolved verification state of one work order item on the detail (SWOOP-250, WOW 2), aligned by
 * position with `workOrder.items`. `verifiedBy` is the stored verifier id resolved to `{ id, name }` (the
 * same shape as a timeline actor), or `null` when the item is unverified or the user no longer resolves.
 */
export interface WorkOrderDetailItem {
  verifiedAt: Date | null;
  verifiedBy: WorkOrderDetailOpenedBy | null;
}

/**
 * The `GET /work-orders/:id` response (SWOOP-326, L7): the stored {@link WorkOrder} (whose `items` are
 * returned as stored) plus the resolved display data — vehicle, garage, opener and, for a scheduled
 * work order, the plan interval. Each relation is `null` when it no longer resolves.
 */
export interface WorkOrderDetail {
  /**
   * The currency amounts captured when this work order is closed will be recorded in (SWOOP-250, WOW 2):
   * resolved server-side from the VEHICLE's CURRENT location → territory → currency — the exact chain
   * `ExpenseService` snapshots when the close books its expense, so the Close dialog labels amounts in the
   * same currency they will be stored in even if the vehicle moved territory since opening. `null` when the
   * vehicle, its location or territory no longer resolves.
   */
  currency: TerritoryCurrency | null;
  /**
   * Who recorded each damage (SWOOP-335), keyed by the damage's id: its stored `createdBy` resolved to
   * `{ id, name }` like a timeline actor, or `null` when the user no longer resolves. Every damage appears.
   * Absent on a response that predates damages.
   */
  damageCreatedBy?: Record<string, WorkOrderDetailOpenedBy | null>;
  /**
   * Who ticked each damage Fixed (SWOOP-335), keyed by the damage's id: its stored `fixedBy` resolved to
   * `{ id, name }` like a timeline actor, or `null` when the user no longer resolves. Only fixed damages
   * appear. Absent on a response that predates damages.
   */
  damageFixedBy?: Record<string, WorkOrderDetailOpenedBy | null>;
  garage: WorkOrderListGarage | null;
  interval: WorkOrderDetailInterval | null;
  /** Per-item verification state, aligned by position with `workOrder.items` (SWOOP-250, WOW 2). */
  items: WorkOrderDetailItem[];
  /** Odometer read when the vehicle returned (km), or null (SWOOP-250). Also on `workOrder.odometerIn`. */
  odometerIn: number | null;
  /** Odometer read when the vehicle left for the garage (km), or null (SWOOP-250). Also on `workOrder.odometerOut`. */
  odometerOut: number | null;
  openedBy: WorkOrderDetailOpenedBy | null;
  /** The lifecycle timeline, newest-first, acting users resolved (SWOOP-250, WOW 2). */
  timeline: WorkOrderDetailTimelineEntry[];
  vehicle: WorkOrderDetailVehicle | null;
  /**
   * The vehicle's warranty end date (ISO), for display on the General tab beside Under warranty
   * (SWOOP-254). The same `warrantyExpiryDate` WOW 1 uses to auto-tick under warranty. `null` when the
   * vehicle has no warranty date or no longer resolves.
   */
  warrantyExpiryDate?: string | null;
  workOrder: WorkOrder;
}
